import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { getContactGroups, estimateContacts } from "./campaignService"
import { FiChevronDown, FiArrowLeft } from "react-icons/fi"

export default function CampaignCreate() {
  const navigate = useNavigate()
  const location = useLocation()
  const dropdownRef = useRef(null)
  
  // Get draft campaign from location state if editing
  const draftCampaign = location.state?.draftCampaign
  const canPrefillFromBuilder = location.state?.fromBuilderBack === true
  const prefillCampaign = draftCampaign || (canPrefillFromBuilder ? location.state?.campaignData : {})
  const isEditMode = !!draftCampaign
  
  const [emailChecked, setEmailChecked] = useState(prefillCampaign?.isEmail || false)
  // const [smsChecked, setSmsChecked] = useState(draftCampaign?.isSms || false)
  
  // Form state - pre-fill with draft data if available
  const [campaignName, setCampaignName] = useState(prefillCampaign?.campaignName || "")
  const [campaignSubject, setCampaignSubject] = useState(prefillCampaign?.campaignSubject || "")
  const [selectedContactGroups, setSelectedContactGroups] = useState(
    Array.isArray(prefillCampaign?.selectedContactGroups)
      ? prefillCampaign.selectedContactGroups
      : prefillCampaign?.contactGroup
        ? (Array.isArray(prefillCampaign.contactGroup) ? prefillCampaign.contactGroup : [prefillCampaign.contactGroup])
        : []
  )
  const [estimatedCount, setEstimatedCount] = useState(prefillCampaign?.estimatedCount || prefillCampaign?.recipientsTotal || prefillCampaign?.recipientCount || 0)
  const [emailBody, setEmailBody] = useState(prefillCampaign?.emailBody || "")
  const [smsBody, setSmsBody] = useState(prefillCampaign?.smsBody || "")
  const [agencyName, setAgencyName] = useState(prefillCampaign?.agencyName || "")
  const [scheduledTime, setScheduledTime] = useState(prefillCampaign?.scheduledTime || "")
  const [scheduleDate, setScheduleDate] = useState("")
  const [scheduleTime, setScheduleTime] = useState("")
  const [sendNow, setSendNow] = useState(typeof prefillCampaign?.sendNow === "boolean" ? prefillCampaign.sendNow : !prefillCampaign?.scheduledTime)
  const [attachments, setAttachments] = useState(Array.isArray(prefillCampaign?.attachments) ? prefillCampaign.attachments : [])
  
  // UI state
  const [contactGroups, setContactGroups] = useState([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [loadingEstimate, setLoadingEstimate] = useState(false)
  const [error, setError] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Parse scheduled time if in edit mode
  useEffect(() => {
    if (prefillCampaign?.scheduledTime) {
      try {
        const date = new Date(prefillCampaign.scheduledTime)
        const dateStr = date.toISOString().split('T')[0] // YYYY-MM-DD
        const timeStr = date.toTimeString().slice(0, 5) // HH:MM
        setScheduleDate(dateStr)
        setScheduleTime(timeStr)
      } catch (err) {
        console.error("Failed to parse scheduled time:", err)
      }
    }
  }, [prefillCampaign?.scheduledTime])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isDropdownOpen && !dropdownRef.current?.contains(e.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isDropdownOpen])

  // Fetch contact groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true)
      try {
        const response = await getContactGroups()
        // Handle both array response and nested response
        const groups = response?.body || response?.body?.contactGroups || response?.contactGroups || response || []
        const groupsArray = Array.isArray(groups) ? groups : []
        setContactGroups(groupsArray)
      } catch (err) {
        console.error("Failed to load contact groups:", err)
        setError("Failed to load contact groups")
      } finally {
        setLoadingGroups(false)
      }
    }
    fetchGroups()
  }, [])

  // Estimate contacts when contact groups change
  useEffect(() => {
    if (selectedContactGroups.length > 0) {
      const fetchEstimate = async () => {
        setLoadingEstimate(true)
        try {
          const response = await estimateContacts(selectedContactGroups)
          // Handle different response formats
          const count = response?.body?.recipients || 
                       response?.recipients || 
                       response?.body?.estimatedCount || 
                       response?.estimatedCount || 
                       0
          setEstimatedCount(count)
        } catch (err) {
          console.error("Failed to estimate contacts:", err)
          setEstimatedCount(0)
        } finally {
          setLoadingEstimate(false)
        }
      }
      fetchEstimate()
    } else {
      setEstimatedCount(0)
    }
  }, [selectedContactGroups])

  // Handle contact group selection
  const handleGroupToggle = (group) => {
    setSelectedContactGroups((prev) => {
      if (prev.includes(group)) {
        return prev.filter((g) => g !== group)
      } else {
        return [...prev, group]
      }
    })
  }

  // Handle select all
  const handleSelectAll = () => {
    if (selectedContactGroups.length === contactGroups.length) {
      setSelectedContactGroups([])
    } else {
      setSelectedContactGroups([...contactGroups])
    }
  }

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || [])
    setAttachments([...attachments, ...files])
  }

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index))
  }

  const handleNext = () => {
    // Validate required fields
    if (!campaignName.trim()) {
      setError("Campaign name is required")
      return
    }
    if (!campaignSubject.trim()) {
      setError("Campaign subject line is required")
      return
    }
    if (selectedContactGroups.length === 0) {
      setError("Please select at least one contact group")
      return
    }
    if (!emailChecked) {
      setError("Please select at least Email")
      return
    }
    if (emailChecked && !emailBody.trim()) {
      setError("Email body is required")
      return
    }
    

    // Store campaign data in session/state for next step
    const campaignData = {
      campaignName,
      campaignSubject,
      selectedContactGroups,
      estimatedCount,
      emailBody,
      smsBody,
      agencyName,
      isEmail: emailChecked,
      sendNow,
      scheduledTime: !sendNow ? `${scheduleDate}T${scheduleTime}` : "",
      attachments,
      // Include campaign ID if editing draft
      ...(isEditMode && draftCampaign?._id && { campaignId: draftCampaign._id }),
      ...(isEditMode && { isEditMode: true }),
    }
    
    // Store in sessionStorage for next step
    sessionStorage.setItem("campaignData", JSON.stringify(campaignData))
    
    navigate("/admin/marketing/campaign-builder", { state: { campaignData } })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/marketing/campaigns")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to Campaigns"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold mb-2">{isEditMode ? "Edit Campaign" : "Campaign Builder"}</h1>
          <p className="text-gray-600 text-sm md:text-base">{isEditMode ? "Update your campaign details" : "Create and send email campaigns"}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm max-w-6xl w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12">
          {/* Left column */}
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium text-gray-800">Campaign Name <span className="text-red-500">*</span></h3>
                <span className="text-xs text-gray-500">{campaignName.length}/100</span>
              </div>
              <input 
                type="text"
                maxLength="100"
                className="w-full border rounded-md px-3 py-2 text-sm" 
                placeholder="Enter campaign name" 
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Max 100 characters</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-800 mb-2">Select Campaign type <span className="text-red-500">*</span></h3>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-gray-700">
                  <input type="checkbox" className="h-4 w-4" checked={emailChecked} onChange={(e)=>setEmailChecked(e.target.checked)} />
                  Email
                </label>
                {/* <label className="flex items-center gap-2 text-gray-700">
                  <input type="checkbox" className="h-4 w-4" checked={smsChecked} onChange={(e)=>setSmsChecked(e.target.checked)} />
                  SMS
                </label> */}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-800">Select Contact Groups <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(select multiple)</span></h3>
              </div>
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  disabled={loadingGroups}
                  className="w-full border rounded-md px-3 py-2 text-sm text-gray-700 bg-white flex items-center justify-between hover:bg-gray-50 disabled:opacity-50"
                >
                  <span>
                    {loadingGroups ? "Loading..." : selectedContactGroups.length === 0 ? "Select contact groups" : `${selectedContactGroups.length} selected`}
                  </span>
                  <FiChevronDown className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {isDropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                    {/* <div className="p-2 border-b border-gray-200">
                      <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedContactGroups.length === contactGroups.length && contactGroups.length > 0}
                          onChange={handleSelectAll}
                        />
                        <span className="text-sm font-medium text-gray-700">Select All</span>
                      </label>
                    </div> */}
                    <div className="divide-y divide-gray-100">
                      {contactGroups.map((group) => (
                        <label key={group} className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={selectedContactGroups.includes(group)}
                            onChange={() => handleGroupToggle(group)}
                          />
                          <span className="text-sm text-gray-700">{group}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {selectedContactGroups.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedContactGroups.map((group) => (
                    <span key={group} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      {group}
                      <button
                        type="button"
                        onClick={() => handleGroupToggle(group)}
                        className="ml-1 hover:text-blue-900"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-800 mb-1">Estimated Number <span className="text-gray-400 font-normal">(auto calculated based on selected groups)</span></h3>
              <input 
                className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50" 
                placeholder="0" 
                value={loadingEstimate ? "Loading..." : estimatedCount}
                readOnly
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-sm font-medium text-gray-800">Campaign Subject Line <span className="text-red-500">*</span></h3>
                <span className="text-xs text-gray-500">{campaignSubject.length}/150</span>
              </div>
              <input 
                type="text"
                maxLength="150"
                className="w-full border rounded-md px-3 py-2 text-sm" 
                placeholder="Enter subject line" 
                value={campaignSubject}
                onChange={(e) => setCampaignSubject(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Max 150 characters</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-800 mb-2">Campaign Schedule</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 text-sm cursor-pointer">
                  <span className="text-gray-700">Send now</span>
                  <input 
                    type="radio" 
                    className="h-4 w-4" 
                    checked={sendNow}
                    onChange={() => setSendNow(true)}
                  />
                </label>
                <label className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 text-sm cursor-pointer">
                  <span className="text-gray-700">Schedule for later</span>
                  <input 
                    type="radio" 
                    className="h-4 w-4" 
                    checked={!sendNow}
                    onChange={() => setSendNow(false)}
                  />
                </label>
                {!sendNow && (
                  <div className="grid grid-cols-2 gap-3">
                    <input 
                      type="date"
                      className="border rounded-md px-3 py-2 text-sm" 
                      placeholder="Date" 
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                    />
                    <input 
                      type="time"
                      className="border rounded-md px-3 py-2 text-sm" 
                      placeholder="Time" 
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-5">
            {emailChecked && (
              <div>
                <h3 className=" font-medium text-gray-800 mb-1">Email Campaign</h3>
                <div className="pt-3 ">

                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm" htmlFor="">Agency Name</label>
                  <span className="text-xs text-gray-500">{agencyName.length}/100</span>
                </div>
                <input 
                  type="text"
                  maxLength="100"
                  className="w-full border rounded-md px-3 py-2 text-sm mb-1" 
                  placeholder="Enter agency Name" 
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                />
                <p className="text-xs text-gray-500 mb-3">Max 100 characters</p>
                </div>
                <div className="relative">
                  <div className="flex justify-between mb-2 py-1">
                    <div>
                      <h3 className="text-sm text-gray-800">Email Body <span className="text-red-500">*</span></h3>
                      <span className="text-xs text-gray-500">{emailBody.length}/5000 characters</span>
                    </div>
                    <div>
                      <label className="  bg-[#0d7ac2] hover:bg-[#0a69a6] text-white text-xs rounded px-3 py-1 cursor-pointer">
                        Upload
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </label>
                    </div>
                  </div>

                  <textarea 
                    rows={6} 
                    maxLength="5000"
                    className="w-full border rounded-md px-3 py-2 text-sm" 
                    placeholder={"Hi {First Name}\nWe hope this message finds you well"} 
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 5000 characters</p>
                 
                </div>
                {attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attachments.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs">
                        <span className="truncate">{file.name}</span>
                        <button 
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* {smsChecked && (
              <div>
                <h3 className=" font-medium pb-1 text-gray-800 mb-1">SMS Campaign</h3>
                <h3 className="text-sm text-gray-800 mb-1">SMS Body</h3>
                <input 
                  className="w-full border rounded-md px-3 py-2 text-sm" 
                  placeholder={"Hi {First Name}. Your visa application is being processed. We'll update you soon."} 
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                />
              </div>
            )} */}

            <div className="pt-2">
              <button 
                onClick={handleNext} 
                className="w-full bg-black text-white py-2.5 rounded-md hover:bg-gray-800 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
